import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, ShieldCheck, KeyRound, Clock3, ArrowLeft, ArrowRight, Check } from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const DOCUMENT_STEPS = [
  "Upload Document",
  "Automatic Verification",
  "Tamper Detection",
  "Policy & Key Validation",
  "Verification Result",
];

const ARTIFACT_STEPS = [
  "Upload Artifact",
  "Automatic Verification",
  "Artifact Integrity Analysis",
  "Policy & Key Validation",
  "Verification Result",
];

const NOT_ENABLED = "Not Enabled";

const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 border-green-500 text-white",
  current: "bg-blue-600 border-blue-600 text-white",
  upcoming: "bg-white border-gray-300 text-gray-500",
};

const STEP_LABEL_CLASS = {
  done: "text-green-600",
  current: "text-blue-600",
  upcoming: "text-gray-500",
};

const BADGE_CLASS = {
  Valid: "bg-green-100 text-green-700",
  [NOT_ENABLED]: "bg-orange-100 text-orange-700",
};
const BADGE_FAILED_CLASS = "bg-red-100 text-red-700";

// Certificate state: "na" (not enabled) | "ok" | "bad"
const CERT_TONE_CLASS = {
  na: "text-orange-500",
  ok: "text-green-600",
  bad: "text-red-600",
};

const CERT_STATUS_LABEL = { na: NOT_ENABLED, ok: "Valid", bad: "Invalid" };
const REVOCATION_LABEL = { na: NOT_ENABLED, ok: "Not Revoked", bad: "Revoked / Invalid" };

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                */
/* -------------------------------------------------------------------------- */

const isNil = (value) => value === null || value === undefined;

// Renders "Not Enabled" for null (not-applicable), otherwise pass/fail label
function statusLabel(val, passLabel, failLabel, naLabel = NOT_ENABLED) {
  if (isNil(val)) return naLabel;
  return val ? passLabel : failLabel;
}

const badgeClass = (status) => BADGE_CLASS[status] || BADGE_FAILED_CLASS;

function getStepStatus(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

function getCertState(certificateValid) {
  if (isNil(certificateValid)) return "na";
  return certificateValid ? "ok" : "bad";
}

// The TSA timestamp comes back as raw ISO 8601 UTC (e.g. "2026-08-27T06:32:49Z").
// Render it in the user's local time, human-readable.
function formatTimestamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

function buildPolicyData(result, isArtifact) {
  return [
    {
      label: "Signature Mode",
      value: result.sign_mode || "Unknown",
      status: result.sign_mode ? "Valid" : "Unknown",
    },
    {
      label: "Blockchain Verification",
      value: statusLabel(result.blockchain_valid, "Verified", "Failed"),
      status: statusLabel(result.blockchain_valid, "Valid", "Failed"),
    },
    {
      label: isArtifact ? "Artifact Hash Match" : "Merkle Root Validation",
      value: statusLabel(result.merkle_valid, "Verified", "Failed"),
      status: statusLabel(result.merkle_valid, "Valid", "Failed"),
    },
  ];
}

function buildCertData(result, isArtifact, noun) {
  return [
    { label: "Certificate Status", value: statusLabel(result.certificate_valid, "Valid", "Invalid") },
    { label: `${noun} Hash`, value: result.cid || "N/A" },
    { label: "IPFS CID", value: result.original_ipfs_cid || "N/A" },
    { label: "Encryption", value: result.encrypted ? "Enabled" : "Disabled" },
    {
      label: isArtifact ? "Signature" : "PQ Signature",
      value: result.pq_signature_valid ? "Verified" : "Failed",
    },
  ];
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function StepProgress({ steps, currentStep }) {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => {
        const status = getStepStatus(index, currentStep);
        const isLast = index === steps.length - 1;

        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {!isLast && (
              <div
                className={`absolute top-5 left-1/2 w-full h-[2px] ${
                  status === "done" ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
            <div
              className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_CIRCLE_CLASS[status]}`}
            >
              {status === "done" ? <Check size={12} /> : index + 1}
            </div>
            <p className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_LABEL_CLASS[status]}`}>
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-gray-50 border rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="text-blue-600" size={26} />
        <h3 className="text-[20px] font-semibold text-gray-700">{title}</h3>
      </div>
      {children}
    </div>
  );
}

const ROW_CLASS = "flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white";

function InfoRow({ label, value, valueClassName = "" }) {
  return (
    <div className={ROW_CLASS}>
      <p className="text-[16px] text-gray-400">{label}</p>
      <p className={`text-[14px] font-semibold text-gray-800 ${valueClassName}`}>{value}</p>
    </div>
  );
}

function CertStateRow({ label, state, labels }) {
  return (
    <div className={ROW_CLASS}>
      <p className="text-[16px] text-gray-400">{label}</p>
      <div className={`flex items-center gap-1 font-semibold text-[14px] ${CERT_TONE_CLASS[state]}`}>
        <CheckCircle2 size={16} /> {labels[state]}
      </div>
    </div>
  );
}

function PolicyValidationCard({ items }) {
  return (
    <SectionCard icon={ShieldCheck} title="Policy Validation">
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-2 bg-white"
          >
            <div>
              <p className="text-[16px] text-gray-400">{item.label}</p>
              <p className="text-[14px] font-semibold text-gray-800">{item.value}</p>
            </div>
            <span className={`px-3 py-1 rounded-lg text-14px font-semibold ${badgeClass(item.status)}`}>
              {item.status}
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function TimestampCard({ result }) {
  const tsaStatus = statusLabel(result.tsa_valid, "Valid", "Failed");

  return (
    <SectionCard icon={Clock3} title="Trusted Timestamp">
      <div className="space-y-2">
        <InfoRow label="Timestamp Authority" value="Quantum TSA" />
        <InfoRow label="Timestamp" value={formatTimestamp(result.timestamp) || "Not Available"} />
        <div className={ROW_CLASS}>
          <p className="text-[16px] text-gray-400">Status</p>
          <span className={`px-3 py-1 rounded-lg text-14px font-semibold ${badgeClass(tsaStatus)}`}>
            {tsaStatus}
          </span>
        </div>
      </div>
    </SectionCard>
  );
}

function KeyCertificateCard({ items, certState }) {
  return (
    <SectionCard icon={KeyRound} title="Key & Certificate Validation">
      <div className="space-y-2">
        {items.map((item) => (
          <InfoRow key={item.label} label={item.label} value={item.value} valueClassName="text-right" />
        ))}
        <CertStateRow label="Certificate Status" state={certState} labels={CERT_STATUS_LABEL} />
        <CertStateRow label="Revocation Status" state={certState} labels={REVOCATION_LABEL} />
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function PKIValidation() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentStep = 3;

  const verificationResult = location.state?.verificationResult || {};

  console.log("PKI PAGE", verificationResult);

  const uploadedFiles = location.state?.uploadedFiles || [];
  const aiScore = location.state?.aiScore || 0;
  const riskScore = location.state?.riskScore ?? 0;
  const classification = location.state?.classification;

  // Artifact verification reuses this wizard; the adapter sets kind: "artifact".
  const isArtifact = verificationResult.kind === "artifact";
  const noun = isArtifact ? "Artifact" : "Document";
  const steps = isArtifact ? ARTIFACT_STEPS : DOCUMENT_STEPS;

  const certState = getCertState(verificationResult.certificate_valid);

  const handleNext = () => {
    navigate("/verification-result", {
      state: { uploadedFiles, aiScore, riskScore, classification, verificationResult },
    });
  };

  const handleCancel = () => {
    if (isArtifact) {
      navigate("/artifact-integrity", { state: { uploadedFiles, verificationResult } });
      return;
    }
    navigate("/ai-tamper-detection", { state: { uploadedFiles, aiScore } });
  };

  return (
    <div className="w-full bg-white mx-auto">
      <StepProgress steps={steps} currentStep={currentStep} />

      {/* CONTENT — two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <PolicyValidationCard items={buildPolicyData(verificationResult, isArtifact)} />
          <TimestampCard result={verificationResult} />
        </div>

        <KeyCertificateCard
          items={buildCertData(verificationResult, isArtifact, noun)}
          certState={certState}
        />
      </div>

      {/* FOOTER BUTTONS */}
      <div className="flex justify-between mt-3">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
        >
          <ArrowLeft size={14} /> Cancel
        </button>
        <button
          onClick={handleNext}
          className="flex items-center gap-1.5 h-[44px] px-6 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition"
        >
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}